<template>
  <form @submit.prevent="submitForm" class="cf-form">
    <!-- ==================== Identity ==================== -->
    <section class="cf-section">
      <div class="section-head">
        <div class="section-title">Identity</div>
        <div class="section-sub">Vendor, client name, and login username.</div>
      </div>
      <div class="section-grid">
        <div class="cf-field" :class="{ 'is-invalid': !vendorName.isValid }">
          <label for="vendorName">Vendor</label>
          <!-- Non-superadmin (vendor self-serve) can't pick a vendor — they
               only manage clients under their OWN vendor. Backend enforces
               this too; the locked-display here just prevents confusion. -->
          <div v-if="!isSuperAdmin" class="cf-locked">
            <span class="cf-locked-val">{{ vendorName.val || ownVendor }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cf-lock">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" />
            </svg>
          </div>
          <select
            v-else
            v-model.trim="vendorName.val"
            @blur="clearValidity('vendorName')"
            id="vendorName"
            class="cf-input"
          >
            <option value="">Select a vendor</option>
            <option v-for="(vendor, index) in filteredVendors" :key="index" :value="vendor.username">{{ vendor.username }}</option>
          </select>
          <div
            v-if="isSuperAdmin && mode == 'update' && vendorName.val !== originalVendor"
            class="cf-notice cf-notice-warn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cf-notice-icon">
              <path d="M12 9v4M12 17h.01" stroke-linecap="round" />
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              Changing vendor: old orders stay with <strong>{{ originalVendor }}</strong>. The new vendor cannot approve pending orders created before this change.
            </div>
          </div>
          <p v-if="!vendorName.isValid" class="cf-error">Vendor must not be empty.</p>
        </div>

        <!-- Client Partner owner (11102026) — SUPERADMIN only; view-only role
             that sees this client's pay-in/pay-out + commission. -->
        <div v-if="isSuperAdmin" class="cf-field">
          <label for="clientPartner">Client Partner (owner)</label>
          <select id="clientPartner" v-model="clientPartnerId" class="cf-input">
            <option value="">None</option>
            <option v-for="p in partnerOptions" :key="p.id" :value="p.id">{{ p.name || p.username }}</option>
          </select>
          <p class="cf-hint" v-if="!vendorName.val">Select a vendor first to choose a partner.</p>
        </div>

        <div class="cf-field" :class="{ 'is-invalid': !clientName.isValid }">
          <label for="clientName">Client Name</label>
          <div v-if="mode == 'update'" class="cf-locked">
            <span class="cf-locked-val">{{ clientName.val }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cf-lock">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" />
            </svg>
          </div>
          <input
            v-else
            type="text"
            id="clientName"
            v-model.trim="clientName.val"
            @blur="clearValidity('clientName')"
            class="cf-input"
            placeholder="e.g. Acme Payments"
          />
          <p v-if="!clientName.isValid" class="cf-error">Client Name must not be empty.</p>
        </div>

        <div class="cf-field" :class="{ 'is-invalid': !username.isValid }">
          <label for="username">Username</label>
          <div v-if="mode == 'update'" class="cf-locked">
            <span class="cf-locked-val">{{ username.val }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cf-lock">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" />
            </svg>
          </div>
          <input
            v-else
            type="text"
            id="username"
            v-model.trim="username.val"
            @blur="clearValidity('username')"
            class="cf-input"
            placeholder="login username"
          />
          <p v-if="!username.isValid" class="cf-error">Username must not be empty.</p>
        </div>

        <div class="cf-field" :class="{ 'is-invalid': !email.isValid }">
          <label for="email">
            Email <span class="cf-optional">optional</span>
          </label>
          <input
            type="email"
            id="email"
            v-model.trim="email.val"
            @blur="clearValidity('email')"
            class="cf-input"
            placeholder="ops@acme.com"
          />
          <p v-if="!email.isValid" class="cf-error">{{ email.msg }}</p>
        </div>
      </div>
    </section>

    <!-- ==================== Access ==================== -->
    <section class="cf-section">
      <div class="section-head">
        <div class="section-title">Access</div>
        <div class="section-sub">
          {{ mode === 'update' ? 'Leave password empty to keep the current one unchanged.' : 'Password used for the client portal login.' }}
        </div>
      </div>
      <div class="section-grid">
        <div class="cf-field cf-field-full" :class="{ 'is-invalid': !password.isValid }">
          <label for="password">Password</label>
          <input
            type="text"
            id="password"
            v-model.trim="password.val"
            @blur="clearValidity('password')"
            class="cf-input"
            :placeholder="mode === 'update' ? 'leave empty to keep unchanged' : 'set a strong password'"
          />
          <p v-if="!password.isValid" class="cf-error">{{ password.msg }}</p>
        </div>
      </div>
    </section>

    <!-- ==================== API Credentials ==================== -->
    <section class="cf-section">
      <div class="section-head">
        <div class="section-title">API Credentials</div>
        <div class="section-sub">Secret Key and API Key are used to sign requests from this client's backend.</div>
      </div>
      <div class="section-grid">
        <div class="cf-field" :class="{ 'is-invalid': !secret.isValid }">
          <label for="secret">Secret Key</label>
          <div class="cf-input-group">
            <input
              type="text"
              id="secret"
              v-model.trim="secret.val"
              @blur="clearValidity('secret')"
              class="cf-input cf-input-mono"
            />
            <button type="button" class="cf-generate" @click="generateField('secret')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cf-gen-icon">
                <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke-linecap="round" />
                <path d="M20 4v5h-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Generate
            </button>
          </div>
          <p v-if="!secret.isValid" class="cf-error">Secret Key must not be empty.</p>
        </div>

        <div class="cf-field" :class="{ 'is-invalid': !xKey.isValid }">
          <label for="xKey">API Key (x-Key)</label>
          <div class="cf-input-group">
            <input
              type="text"
              id="xKey"
              v-model.trim="xKey.val"
              @blur="clearValidity('xKey')"
              class="cf-input cf-input-mono"
            />
            <button type="button" class="cf-generate" @click="generateField('xKey')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cf-gen-icon">
                <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke-linecap="round" />
                <path d="M20 4v5h-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Generate
            </button>
          </div>
          <p v-if="!xKey.isValid" class="cf-error">xKey must not be empty.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Webhooks ==================== -->
    <section class="cf-section">
      <div class="section-head">
        <div class="section-title">Webhooks</div>
        <div class="section-sub">Callback endpoints — we POST here on order state changes.</div>
      </div>
      <div class="section-grid">
        <div class="cf-field" :class="{ 'is-invalid': !callbackURL.isValid }">
          <label for="callbackURL">Pay-In Callback URL</label>
          <input
            type="text"
            id="callbackURL"
            v-model.trim="callbackURL.val"
            @blur="clearValidity('callbackURL')"
            class="cf-input"
            placeholder="https://…/webhooks/payin"
          />
          <p v-if="!callbackURL.isValid" class="cf-error">{{ callbackURL.msg }}</p>
        </div>

        <div class="cf-field" :class="{ 'is-invalid': !payOutCallbackURL.isValid }">
          <label for="payOutCallbackURL">Pay-Out Callback URL</label>
          <input
            type="text"
            id="payOutCallbackURL"
            v-model.trim="payOutCallbackURL.val"
            @blur="clearValidity('payOutCallbackURL')"
            class="cf-input"
            placeholder="https://…/webhooks/payout"
          />
          <p v-if="!payOutCallbackURL.isValid" class="cf-error">{{ payOutCallbackURL.msg }}</p>
        </div>

        <div class="cf-field" :class="{ 'is-invalid': !failedOrderCallbackURL.isValid }">
          <label for="failedOrderCallbackURL">Failed Order Callback URL</label>
          <input
            type="text"
            id="failedOrderCallbackURL"
            v-model.trim="failedOrderCallbackURL.val"
            @blur="clearValidity('failedOrderCallbackURL')"
            class="cf-input"
            placeholder="https://…/webhooks/failed"
          />
          <p v-if="!failedOrderCallbackURL.isValid" class="cf-error">{{ failedOrderCallbackURL.msg }}</p>
        </div>

        <!-- Expired-order webhook opt-in (29072026). Lives beside the
             callback URL fields because it reuses the Pay-In Callback URL
             (per-order callback_url still wins when present). Default OFF
             so existing integrations never see a new event un-asked. -->
        <label class="cf-toggle-row" :class="{ 'is-on': expiredCallbackEnabled, 'is-off': !expiredCallbackEnabled }">
          <div class="cf-toggle-copy">
            <div class="cf-toggle-title">
              Expired Order Callback
              <span :class="expiredCallbackEnabled ? 'cf-toggle-badge cf-toggle-badge-on' : 'cf-toggle-badge cf-toggle-badge-off'">
                {{ expiredCallbackEnabled ? 'ENABLED' : 'DISABLED' }}
              </span>
            </div>
            <div class="cf-toggle-desc">
              When on, expired pay-in orders send a webhook with status "expired" to the Pay-In Callback URL (a per-order callback URL, when supplied at creation, takes priority). Pay-in only — payouts never expire.
            </div>
          </div>
          <span class="cf-switch" @click.prevent="expiredCallbackEnabled = !expiredCallbackEnabled">
            <input type="checkbox" v-model="expiredCallbackEnabled" class="cf-switch-input" />
            <span class="cf-switch-track"></span>
            <span class="cf-switch-knob"></span>
          </span>
        </label>

        <div class="cf-field" :class="{ 'is-invalid': !walletCallBackURL.isValid }">
          <label for="walletCallBackURL">Wallet Callback URL</label>
          <input
            type="text"
            id="walletCallBackURL"
            v-model.trim="walletCallBackURL.val"
            @blur="clearValidity('walletCallBackURL')"
            class="cf-input"
            placeholder="https://…/webhooks/wallet"
          />
          <p v-if="!walletCallBackURL.isValid" class="cf-error">{{ walletCallBackURL.msg }}</p>
        </div>
      </div>
    </section>

    <!-- ==================== Commission ==================== -->
    <!-- Commission rates the VENDOR charges THIS client per order.
         Stored on the client's users row (role='client') in payInCommission
         / payOutCommission columns. Optional — defaults to 0 (no commission)
         when left blank. -->
    <section class="cf-section">
      <div class="section-head">
        <div class="section-title">Commission</div>
        <div class="section-sub">Percentage the vendor charges this client per order. Leave blank for none.</div>
      </div>
      <div class="section-grid">
        <div class="cf-field" :class="{ 'is-invalid': !payInCommission.isValid }">
          <label for="payInCommission">Pay-In Commission</label>
          <div class="cf-input-suffix">
            <input
              type="number" step="0.01" min="0" max="100"
              id="payInCommission"
              v-model.number="payInCommission.val"
              @blur="clearValidity('payInCommission')"
              class="cf-input cf-input-num"
              placeholder="0.00"
            />
            <span class="cf-suffix">%</span>
          </div>
          <p v-if="!payInCommission.isValid" class="cf-error">{{ payInCommission.msg }}</p>
        </div>

        <div class="cf-field" :class="{ 'is-invalid': !payOutCommission.isValid }">
          <label for="payOutCommission">Pay-Out Commission</label>
          <div class="cf-input-suffix">
            <input
              type="number" step="0.01" min="0" max="100"
              id="payOutCommission"
              v-model.number="payOutCommission.val"
              @blur="clearValidity('payOutCommission')"
              class="cf-input cf-input-num"
              placeholder="0.00"
            />
            <span class="cf-suffix">%</span>
          </div>
          <p v-if="!payOutCommission.isValid" class="cf-error">{{ payOutCommission.msg }}</p>
        </div>
      </div>
    </section>

    <!-- ==================== Payout kill-switch ==================== -->
    <!-- Per-client payout kill-switch. Off = every new payout order creation
         path (API, portal, admin on-behalf-of) refuses this client with a
         "Payout is disabled" error. Existing in-flight orders unaffected. -->
    <!-- ==================== Pay-In Control ==================== -->
    <section class="cf-section">
      <div class="section-head">
        <div class="section-title">Pay-In Control</div>
        <div class="section-sub">Behaviour switches for this client's pay-in orders and customer payment pages.</div>
      </div>

      <!-- Single-active-payin-per-customer supersede opt-in (09082026).
           For clients whose systems only keep ONE live pay-in per
           customer. Default OFF. -->
      <label class="cf-toggle-row" :class="{ 'is-on': singleActivePayin, 'is-off': !singleActivePayin }">
        <div class="cf-toggle-copy">
          <div class="cf-toggle-title">
            Single Active Pay-In per Customer
            <span :class="singleActivePayin ? 'cf-toggle-badge cf-toggle-badge-on' : 'cf-toggle-badge cf-toggle-badge-off'">
              {{ singleActivePayin ? 'ENABLED' : 'DISABLED' }}
            </span>
          </div>
          <div class="cf-toggle-desc">
            When on, a new API pay-in for a customer (matched by customer mobile) automatically fails the customer's older pending pay-ins that have no UTR/screenshot yet — the old payment pages stop accepting proof and point the customer to the new page. Each failed order sends the normal failed-order callback. Orders that already carry proof are never touched.
          </div>
        </div>
        <span class="cf-switch" @click.prevent="singleActivePayin = !singleActivePayin">
          <input type="checkbox" v-model="singleActivePayin" class="cf-switch-input" />
          <span class="cf-switch-track"></span>
          <span class="cf-switch-knob"></span>
        </span>
      </label>

      <!-- Customer QR-switch opt-in (09082026). Payment-page carousel:
           change QR + slide between assigned QRs. Default OFF. -->
      <label class="cf-toggle-row" :class="{ 'is-on': qrSwitchEnabled, 'is-off': !qrSwitchEnabled }">
        <div class="cf-toggle-copy">
          <div class="cf-toggle-title">
            Customer QR Switch
            <span :class="qrSwitchEnabled ? 'cf-toggle-badge cf-toggle-badge-on' : 'cf-toggle-badge cf-toggle-badge-off'">
              {{ qrSwitchEnabled ? 'ENABLED' : 'DISABLED' }}
            </span>
          </div>
          <div class="cf-toggle-desc">
            When on, this client's customers can tap "QR not working? Change QR" on the payment page (up to 3 QRs per order) and slide freely between the QRs already offered. Only the QR currently shown is payable; each switch is re-validated and may change the payable amount. Off = payment page behaves exactly as before.
          </div>
        </div>
        <span class="cf-switch" @click.prevent="qrSwitchEnabled = !qrSwitchEnabled">
          <input type="checkbox" v-model="qrSwitchEnabled" class="cf-switch-input" />
          <span class="cf-switch-track"></span>
          <span class="cf-switch-knob"></span>
        </span>
      </label>
    </section>

    <section class="cf-section">
      <div class="section-head">
        <div class="section-title">Payout Control</div>
        <div class="section-sub">Kill-switch for new payout orders. Existing orders in flight are unaffected.</div>
      </div>
      <label class="cf-toggle-row" :class="{ 'is-on': payoutEnabled, 'is-off': !payoutEnabled }">
        <div class="cf-toggle-copy">
          <div class="cf-toggle-title">
            Payout <span :class="payoutEnabled ? 'cf-toggle-badge cf-toggle-badge-on' : 'cf-toggle-badge cf-toggle-badge-off'">
              {{ payoutEnabled ? 'ENABLED' : 'DISABLED' }}
            </span>
          </div>
          <div class="cf-toggle-desc">
            When off, every payout creation path refuses this client's new orders with a "Payout is disabled" error.
          </div>
        </div>
        <span class="cf-switch" @click.prevent="payoutEnabled = !payoutEnabled">
          <input type="checkbox" v-model="payoutEnabled" class="cf-switch-input" />
          <span class="cf-switch-track"></span>
          <span class="cf-switch-knob"></span>
        </span>
      </label>
    </section>

    <!-- ==================== Submit ==================== -->
    <div class="cf-submit">
      <p v-if="!formIsValid" class="cf-submit-error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
        </svg>
        Please fix the highlighted errors and submit again.
      </p>
      <button type="submit" class="btn btn-primary">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="btn-icon">
          <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ mode === 'create' ? 'Create Client' : 'Save Changes' }}
      </button>
    </div>
  </form>
</template>

<script>
import http from "../../http-common";

export default {
  name: "client-form",
  emits: ["create-client"],
  props: ["mode", "selectedClient"],
  watch: {
    // Load the selected vendor's Client Partners for the owner dropdown
    // (superadmin only). Reset the chosen owner when the USER changes vendor
    // (oldV truthy) — not on the initial set during edit-seed (oldV empty).
    "vendorName.val": function (newV, oldV) {
      if (!this.isSuperAdmin) return;
      if (oldV && newV !== oldV) this.clientPartnerId = "";
      this.loadPartners();
    },
  },
  created() {
    if (this.isSuperAdmin) this.getVendors();
  },
  mounted() {
    if (this.mode == "update") {
      this.setClient();
    } else if (!this.isSuperAdmin) {
      // Pre-fill vendor for non-superadmin create flow — the input is
      // locked, so this is what'll be submitted.
      this.vendorName.val = this.ownVendor;
    }
  },
  data() {
    return {
      vendorName: { val: "", isValid: true, msg: "" },
      clientName: { val: "", isValid: true, msg: "" },
      username: { val: "", isValid: true, msg: "" },
      email: { val: "", isValid: true, msg: "" },
      password: { val: "", isValid: true, msg: "" },
      secret: { val: "", isValid: true, msg: "" },
      xKey: { val: "", isValid: true, msg: "" },
      callbackURL: { val: "", isValid: true, msg: "" },
      payOutCallbackURL: { val: "", isValid: true, msg: "" },
      walletCallBackURL: { val: "", isValid: true, msg: "" },
      instantPayoutLimit: { val: "", isValid: true, msg: "" },
      failedOrderCallbackURL: { val: "", isValid: true, msg: "" },
      is_instant_payout: { val: 0, isValid: true, msg: "" },
      payInCommission: { val: "", isValid: true, msg: "" },
      payOutCommission: { val: "", isValid: true, msg: "" },
      // Per-client payout on/off (07072026). Defaults to true (enabled)
      // on create — vendor explicitly disables when needed. Loaded from
      // selectedClient.payout_enabled on edit.
      payoutEnabled: true,
      // Expired-order webhook opt-in (29072026). Defaults to FALSE on
      // create — expiries stay silent until deliberately enabled, so
      // active integrations never receive a surprise event type.
      expiredCallbackEnabled: false,
      singleActivePayin: false,
      qrSwitchEnabled: false,
      originalVendor: "",
      // Client Partner ownership (11102026) — SUPERADMIN only. The owner is
      // chosen from the selected vendor's partners; backend strips the field
      // for any non-superadmin caller.
      clientPartnerId: "",
      partnerOptions: [],
      formIsValid: true,
    };
  },
  computed: {
    filteredVendors() {
      return this.$store.getters["vendor/users"];
    },
    isSuperAdmin() {
      return !!this.$store.getters.isSuperAdmin;
    },
    ownVendor() {
      return this.$store.getters.vendor || "";
    },
  },
  methods: {
    async getVendors() {
      try {
        await this.$store.dispatch("vendor/getUsers");
      } catch (error) {
        console.log(error.message);
      }
    },
    // Client Partners for the owner dropdown, scoped to the selected vendor.
    async loadPartners() {
      if (!this.isSuperAdmin) return;
      const vendor = this.vendorName.val;
      if (!vendor) { this.partnerOptions = []; return; }
      try {
        const r = await http.get("/client-partners", { params: { vendor } });
        this.partnerOptions = (r.data && r.data.data) || [];
      } catch (e) {
        this.partnerOptions = [];
      }
    },
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    // Generate a random URL-safe string for API credentials (Secret Key / x-Key).
    // Uses crypto.getRandomValues so values are suitable as secrets, with a
    // Math.random fallback for the rare browser that lacks SubtleCrypto exposure.
    randomToken(len = 40) {
      const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let out = "";
      if (window.crypto && window.crypto.getRandomValues) {
        const buf = new Uint32Array(len);
        window.crypto.getRandomValues(buf);
        for (let i = 0; i < len; i++) out += alphabet[buf[i] % alphabet.length];
      } else {
        for (let i = 0; i < len; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
      }
      return out;
    },
    generateField(field) {
      this[field].val = this.randomToken(field === "xKey" ? 32 : 48);
      this.clearValidity(field);
    },
    validateForm() {
      this.formIsValid = true;

      if (this.mode == "create") {
        if (this.vendorName.val === "") {
          this.vendorName.isValid = false;
          this.formIsValid = false;
        }
        if (this.clientName.val === "") {
          this.clientName.isValid = false;
          this.formIsValid = false;
        }
        if (this.username.val === "") {
          this.username.isValid = false;
          this.formIsValid = false;
        }
        if (this.password.val === "") {
          this.password.isValid = false;
          this.password.msg = "Password must not be empty.";
          this.formIsValid = false;
        }
      }

      // Email is optional for clients. When provided, validate the shape so
      // we don't store junk; empty is accepted and stored as NULL by the API.
      if (this.email.val !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.val)) {
        this.email.isValid = false;
        this.email.msg = "Please enter a valid email address.";
        this.formIsValid = false;
      }
      // Instant Payment / Instant Payout Limit validation skipped — fields
      // are hidden (feature deprecated) and defaulted at submit time.
      if (this.secret.val === "") {
        this.secret.isValid = false;
        this.formIsValid = false;
      }
      if (this.xKey.val === "") {
        this.xKey.isValid = false;
        this.formIsValid = false;
      }
      if (this.callbackURL.val === "") {
        this.callbackURL.isValid = false;
        this.callbackURL.msg = "Pay In Callback URL must not be empty.";
        this.formIsValid = false;
      }
      if (this.walletCallBackURL.val === "") {
        this.walletCallBackURL.isValid = false;
        this.walletCallBackURL.msg = "Wallet Callback must not be empty.";
        this.formIsValid = false;
      }
      if (this.payOutCallbackURL.val === "") {
        this.payOutCallbackURL.isValid = false;
        this.payOutCallbackURL.msg = "Pay Out Callback URL must not be empty.";
        this.formIsValid = false;
      }
      if (this.failedOrderCallbackURL.val === "") {
        this.failedOrderCallbackURL.isValid = false;
        this.failedOrderCallbackURL.msg = "Failed Order Callback URL must not be empty.";
        this.formIsValid = false;
      }
    },
    async submitForm() {
      this.validateForm();

      if (!this.formIsValid) {
        return;
      }

      let formData = {
        vendor: this.vendorName.val,
        clientName: this.clientName.val,
        email: this.email.val,
        secret: this.secret.val,
        xKey: this.xKey.val,
        callbackURL: this.callbackURL.val,
        wallet_callback: this.walletCallBackURL.val,
        payOutCallbackURL: this.payOutCallbackURL.val,
        failedOrderCallbackURL: this.failedOrderCallbackURL.val,
        // Instant payout feature is deprecated — always submit safe defaults
        // so the backend create/update payload contract stays unchanged.
        is_instant_payout: 0,
        instant_payout_limit: this.instantPayoutLimit.val || "0",
        // Blank → 0 so the vendor can leave commission unset (no charge).
        payInCommission: this.payInCommission.val === "" ? 0 : parseFloat(this.payInCommission.val),
        payOutCommission: this.payOutCommission.val === "" ? 0 : parseFloat(this.payOutCommission.val),
        // Per-client payout kill-switch. Always sent so both create + update
        // paths honour the current state of the toggle.
        payout_enabled: this.payoutEnabled ? 1 : 0,
        // Expired-order webhook opt-in — always sent, same rationale.
        expired_callback_enabled: this.expiredCallbackEnabled ? 1 : 0,
        single_active_payin_per_customer: this.singleActivePayin ? 1 : 0,
        qr_switch_enabled: this.qrSwitchEnabled ? 1 : 0,
        // Client Partner owner — only superadmin sends it ("" = unassign).
        // Backend strips it for non-superadmin callers regardless.
        ...(this.isSuperAdmin ? { client_partner_id: this.clientPartnerId || "" } : {}),
      };

      if (this.mode == "create") {
        formData.username = this.username.val;
        formData.password = this.password.val;
      } else {
        // password optional on update
        if (this.password.val !== "") {
          formData.password = this.password.val;
        }
      }

      // Double-confirmation when a vendor reassignment is part of this update.
      // Old orders stay with the old vendor (per spec decision 4A) so the
      // superadmin needs to understand the tradeoff before it's written.
      if (this.mode == "update" && this.vendorName.val !== this.originalVendor) {
        const first = await this.$swal.fire({
          title: "Change vendor?",
          html: `You are moving <strong>${this.clientName.val}</strong> from <strong>${this.originalVendor}</strong> to <strong>${this.vendorName.val}</strong>.<br/><br/>Old orders will remain with <strong>${this.originalVendor}</strong>. Pending orders created before this change <strong>cannot be approved by the new vendor</strong>.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#6b7280",
          confirmButtonText: "Continue",
        });
        if (!first.isConfirmed) return;

        const second = await this.$swal.fire({
          title: "Are you absolutely sure?",
          html: `This cannot be undone by another edit — the audit log will record it.<br/><br/>Type the new vendor name <strong>${this.vendorName.val}</strong> to confirm.`,
          icon: "warning",
          input: "text",
          inputPlaceholder: this.vendorName.val,
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#6b7280",
          confirmButtonText: "Change vendor",
          inputValidator: (value) => {
            if (value !== this.vendorName.val) {
              return "Vendor name doesn't match.";
            }
          },
        });
        if (!second.isConfirmed) return;
      }

      this.$emit("create-client", formData);
    },
    setClient() {
      this.vendorName.val = this.selectedClient.vendor;
      this.originalVendor = this.selectedClient.vendor;
      this.clientName.val = this.selectedClient.clientName;
      this.username.val = this.selectedClient.username || "";
      this.email.val = this.selectedClient.email || "";
      this.secret.val = this.selectedClient.secret;
      this.xKey.val = this.selectedClient.xKey;
      this.callbackURL.val = this.selectedClient.callbackURL;
      this.payOutCallbackURL.val = this.selectedClient.payOutCallbackURL;
      this.failedOrderCallbackURL.val = this.selectedClient.failedOrderCallbackURL;
      this.is_instant_payout.val = this.selectedClient.is_instant_payout;
      this.walletCallBackURL.val = this.selectedClient.wallet_callback;
      this.instantPayoutLimit.val = this.selectedClient.instant_payout_limit;
      this.payInCommission.val = this.selectedClient.payInCommission ?? "";
      this.payOutCommission.val = this.selectedClient.payOutCommission ?? "";
      // TINYINT(1) → number; coerce to bool for the checkbox. Missing on
      // legacy client rows before the migration → treat as enabled (default).
      const pe = this.selectedClient.payout_enabled;
      this.payoutEnabled = pe == null ? true : Number(pe) === 1;
      // Expired webhook opt-in: missing (pre-migration rows) → DISABLED,
      // matching the column default of 0.
      const ec = this.selectedClient.expired_callback_enabled;
      this.expiredCallbackEnabled = ec == null ? false : Number(ec) === 1;
      // Single-active-payin opt-in: missing (pre-migration rows) → DISABLED,
      // matching the column default of 0.
      const sap = this.selectedClient.single_active_payin_per_customer;
      this.singleActivePayin = sap == null ? false : Number(sap) === 1;
      // QR-switch opt-in: missing (pre-migration rows) → DISABLED.
      const qs = this.selectedClient.qr_switch_enabled;
      this.qrSwitchEnabled = qs == null ? false : Number(qs) === 1;
      // Client Partner owner (superadmin view) — seed from the row.
      this.clientPartnerId = this.selectedClient.client_partner_id || "";
    },
  },
};
</script>

<style scoped>
/* ==================== Form shell ==================== */
.cf-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 20px 20px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
}

/* ==================== Section ==================== */
.cf-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 22px;
}
.section-head {
  margin-bottom: 16px;
}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.section-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}

/* ==================== Grid ==================== */
.section-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
}
@media (max-width: 720px) {
  .section-grid { grid-template-columns: 1fr; }
}

/* ==================== Field ==================== */
.cf-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.cf-field-full { grid-column: 1 / -1; }
.cf-field label {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.cf-optional {
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.04em;
  text-transform: none;
}

/* ==================== Input ==================== */
.cf-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;
  color: #0f172a;
}
.cf-input::placeholder { color: #94a3b8; }
.cf-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
select.cf-input {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}
.cf-input-mono { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 12px; }
.cf-input-num { font-variant-numeric: tabular-nums; }

/* Invalid state */
.cf-field.is-invalid .cf-input,
.cf-field.is-invalid .cf-locked,
.cf-field.is-invalid .cf-input-group,
.cf-field.is-invalid .cf-input-suffix {
  border-color: #ef4444;
  background: #fef2f2;
}
.cf-field.is-invalid .cf-input:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}
.cf-error {
  font-size: 11px;
  color: #dc2626;
  margin-top: 2px;
  font-weight: 500;
}

/* Locked (read-only display) */
.cf-locked {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
}
.cf-locked-val {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cf-lock {
  width: 14px; height: 14px;
  color: #94a3b8;
  flex-shrink: 0;
}

/* Input + Generate group */
.cf-input-group {
  display: flex;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  overflow: hidden;
  transition: border-color 0.15s;
}
.cf-input-group:focus-within {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.cf-input-group .cf-input {
  border: none;
  background: transparent;
  border-radius: 0;
  flex: 1;
  box-shadow: none;
}
.cf-input-group .cf-input:focus {
  background: transparent;
  box-shadow: none;
}
.cf-generate {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 14px;
  background: #eef2f7;
  color: #1B2A47;
  border: none;
  border-left: 1px solid #e2e8f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.12s;
}
.cf-generate:hover { background: #e0e7ff; }
.cf-gen-icon { width: 12px; height: 12px; }

/* Input with suffix (like %) */
.cf-input-suffix {
  display: flex;
  align-items: stretch;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  overflow: hidden;
  transition: border-color 0.15s;
}
.cf-input-suffix:focus-within {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.cf-input-suffix .cf-input {
  border: none;
  background: transparent;
  border-radius: 0;
  flex: 1;
  box-shadow: none;
}
.cf-suffix {
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border-left: 1px solid #e2e8f0;
}

/* Notice (vendor-change warning) */
.cf-notice {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.4;
}
.cf-notice-warn {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}
.cf-notice-warn strong { color: #78350f; }
.cf-notice-icon {
  width: 16px; height: 16px;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ==================== Toggle row ==================== */
.cf-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.15s;
}
.cf-toggle-row.is-on {
  border-color: #a7f3d0;
  background: #ecfdf5;
}
.cf-toggle-row.is-off {
  border-color: #fecaca;
  background: #fef2f2;
}
.cf-toggle-copy { min-width: 0; }
.cf-toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.cf-toggle-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.cf-toggle-badge-on { background: #d1fae5; color: #065f46; }
.cf-toggle-badge-off { background: #fee2e2; color: #991b1b; }
.cf-toggle-desc {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* iOS-style toggle switch */
.cf-switch {
  position: relative;
  display: inline-block;
  width: 44px; height: 24px;
  flex-shrink: 0;
  cursor: pointer;
}
.cf-switch-input {
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  position: absolute;
}
.cf-switch-track {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.18s;
}
.cf-switch-knob {
  position: absolute;
  top: 2px; left: 2px;
  width: 20px; height: 20px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.2);
  transition: transform 0.18s;
}
.cf-switch-input:checked ~ .cf-switch-track { background: #10b981; }
.cf-switch-input:checked ~ .cf-switch-knob  { transform: translateX(20px); }

/* ==================== Submit ==================== */
.cf-submit {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  padding: 8px 0 4px;
}
.cf-submit-error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 500;
  align-self: stretch;
  justify-content: center;
}
.cf-submit-error svg { width: 14px; height: 14px; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }
</style>
